/**
 * Install steps per downloadable platform - the "Getting Started on <Platform>"
 * block. One list, three surfaces: the theme/content page banner, the cart
 * (before paying) and the order page (after paying). Keyed by the platform's
 * display name lower-cased ('sublime text'); `gettingStartedFor` also accepts
 * the store's platform key ('sublime-text').
 *
 * The free platforms say "Download button" / "Copy button" (the file comes from
 * the page); the same steps read fine on the order page, where the Download
 * button is the purchased zip.
 */
export const GETTING_STARTED: { [ platform: string ]: string[] } = {
  // the store's macOS apps (2026-10-14: the Color Picker, signed with Developer ID and notarized by Apple)
  macos: [
    'Download the zip with the Download button and double-click it: macOS unpacks the app next to it.',
    'Drag the app into your Applications folder.',
    'Open it from Applications or Spotlight. It is signed and notarized by Apple, so it opens without a warning; it lives in the menu bar, not in the Dock.',
    'A new version is on your order page as soon as it is released: download it the same way and replace the app in Applications.',
  ],
  // the free themes' color palettes, through the cart since 2026-10-12 (keyed by the store platform
  // 'figma-palette' etc.); the zip holds the one palette file
  'figma palette': [
    'Download the zip with the Download button. Inside is the palette as a .tokens.json file.',
    'In Figma, open the Tokens Studio for Figma plugin ( install it from the Community once ).',
    'In the plugin settings, turn on the DTCG token format, then choose Import and pick the .tokens.json file.',
    'Apply the set and push the 24 named colors to your Figma variables or styles.',
  ],
  'coreldraw palette': [
    'Download the zip with the Download button. Inside is the palette as an .xml file.',
    'In CorelDRAW, open the Palette Manager: Window → Dockers → Palette Manager.',
    'Choose Open Palette from the docker menu and pick the .xml file.',
    'The palette docks with all 24 named Codigrate colors ready to use.',
  ],
  'macos palette': [
    'Download the zip with the Download button. Inside is the palette as a .clr file.',
    'Copy the .clr file into ~/Library/Colors ( in Finder, Shift+Cmd+G takes you to the path ).',
    'Open the color picker in any app, for example Cmd+Shift+C in TextEdit, or click any color well.',
    'Pick the palette in the picker\'s Palettes tab, in Keynote, Pages, Xcode and every app that uses the system colors.',
  ],
  // Claude Code (2026-10-16): the theme colours Claude Code's own interface; the code colours come from the terminal
  'claude code': [
    'Download the theme with the Download button. It is one file named after the theme, '
    + 'for example codigrate-london.json; keep the name as is.',
    'Move the file into your Claude Code themes folder, ~/.claude/themes/. If the folder is not there yet, '
    + 'create it in a terminal: mkdir -p ~/.claude/themes',
    'In Claude Code, type /theme and pick the theme from the list, for example Codigrate London. '
    + 'It applies at once.',
    'For the best result, install the same theme for your terminal too ( Ghostty, iTerm2 or macOS Terminal ): '
    + 'Claude Code colors code with your terminal\'s colors, so with the matching terminal theme '
    + 'the code reads like the theme in your IDE.',
  ],
  // OpenCode (2026-10-28): the theme paints OpenCode's whole interface, its own background included
  opencode: [
    'Download the theme with the Download button. It is one file named after the theme, '
    + 'for example codigrate-london.json; keep the name as is.',
    'Move the file into your OpenCode themes folder, ~/.config/opencode/themes/. If the folder is not there yet, '
    + 'create it in a terminal: mkdir -p ~/.config/opencode/themes',
    'In OpenCode, type /theme and pick the theme from the list, for example codigrate-london.',
    'Your terminal needs 24-bit color: run echo $COLORTERM and look for truecolor or 24bit.',
  ],
  // Codex CLI (2026-10-28): the theme colors code, diffs, Markdown and the accent; the background stays the terminal's
  codex: [
    'Download the theme with the Download button. It is one file named after the theme, '
    + 'for example codigrate-london.tmTheme; keep the name as is.',
    'Move the file into your Codex themes folder, ~/.codex/themes/. If the folder is not there yet, '
    + 'create it in a terminal: mkdir -p ~/.codex/themes',
    'In Codex, type /theme and pick the theme from the list, for example codigrate-london.',
    'Codex keeps your terminal\'s background: for the full look, set your terminal\'s background to the theme\'s own.',
  ],
  ghostty: [
    'Download the theme with the Download button. It is one file named after the theme, '
    + 'for example Codigrate London; keep the name as is.',
    'Move the file into your Ghostty themes folder, ~/.config/ghostty/themes/ '
    + '( the same path on macOS and Linux ). If the folder is not there yet, create it in a terminal: '
    + 'mkdir -p ~/.config/ghostty/themes ( on a Mac, Shift+Cmd+G in Finder takes you to the path ).',
    'Open your Ghostty config: in Ghostty press Cmd+, ( Ctrl+, on Linux ), and the config file '
    + 'opens in your editor ( Ghostty creates it if you have none yet ). '
    + 'Add a line: theme = Codigrate London ( the file name ).',
    'Save, then press Shift+Cmd+, ( Ctrl+Shift+, on Linux ) in Ghostty to reload the config, '
    + 'and the theme is applied.',
  ],
  iterm2: [
    'Download the .itermcolors file with the Download button.',
    'In iTerm2, open Settings → Profiles → Colors.',
    'Open the Color Presets… dropdown at the bottom right, choose Import…, '
    + 'and select the downloaded file.',
    'Open the Color Presets… dropdown again and pick the theme by name, '
    + 'and the theme is applied.',
  ],
  slack: [
    'Download the zip with the Download button. Inside is a small text file with the theme\'s four colors on one line.',
    'Open the text file and copy that line.',
    'In Slack, open Preferences → Themes, scroll to the bottom and paste the line into the custom theme field.',
    'In Preferences → Appearance, choose Light or Dark mode to match the theme; in Dark mode, turn on Darker sidebars.',
    'If you switch between light and dark mode, restart Slack so the theme applies correctly.',
    'Slack applies the theme instantly across the workspace.',
  ],
  'macos terminal': [
    'Download the .terminal profile with the Download button.',
    'Double-click the downloaded file to import it into Terminal '
    + '( or, in Terminal, go to Settings → Profiles → the ⋯ menu → Import… '
    + 'and choose the file ).',
    'With the imported profile selected, click Default to set it as your '
    + 'default profile.',
    'Open a new Terminal window, and the theme is applied.',
  ],
  rhino: [
    'Download the zip with the Download button. Inside are the Mac one-click app ( Codigrate <Name> Rhino Theme.app ) '
    + 'and the Windows color file ( Codigrate <Name>.ini ).',
    'On a Mac, double-click the app and confirm you want to open it. It closes Rhino, applies the full theme '
    + '( viewport, toolbars, panels, tabs ), matches your macOS light or dark appearance to the theme, and reopens Rhino. '
    + 'The app is signed and notarized by Apple, so macOS opens it without a warning.',
    'On Windows, run the OptionsImport command ( or Tools → Import Options… ) and choose the .ini file.',
    'Tick Appearance, Grid and UiPaintColors and click Apply; the viewport and the toolbar and tab colors update at once.',
    'Rhino draws a few controls with the system appearance, so keep Windows in light mode for a light theme and dark mode for a dark theme.',
  ],
  xcode: [
    'Download the zip with the Download button. Inside are two theme files: one for Xcode 26 ( .xcworkspacecolortheme ) and one for Xcode 16 and earlier ( .xccolortheme ).',
    'Copy both files into ~/Library/Developer/Xcode/UserData/FontAndColorThemes/ ( in Finder, Shift+Cmd+G takes you to the path; create the folder if it is not there ).',
    'Xcode 26: open Settings → Appearance, click Choose next to Theme and pick the theme under Custom. Xcode 16 and earlier: Settings → Themes lists it after a restart.',
    'The theme paints the source editor, the console and the documentation view; the window keeps the macOS look.',
  ],
  zed: [
    'Download the zip with the Download button. Inside is one folder with the theme file ( themes/<name>.json ) and a small extension.toml.',
    'Copy the .json file from the themes folder into ~/.config/zed/themes/ ( the same path on macOS and Linux; on a Mac, Shift+Cmd+G in Finder takes you to the path ). If the folder is not there yet, create it.',
    'In Zed, open the theme selector with Cmd+K Cmd+T ( Ctrl+K Ctrl+T on Linux ) and pick the theme by name, and the theme is applied.',
    'Prefer an extension? In Zed open Extensions, click Install Dev Extension and choose the unzipped folder itself; the theme then shows in the selector the same way.',
  ],
  'sublime text': [
    'Download the zip with the Download button. Inside is one folder with the color scheme and the window theme.',
    'In Sublime Text, open Preferences → Browse Packages…. Your Packages folder opens in Finder / Explorer.',
    'Unzip the download and drag its folder into that Packages folder. Sublime Text picks it up at once, no restart.',
    'Back in Sublime Text, open Preferences → Select Color Scheme… and choose the theme to paint the editor; then Preferences → Select Theme… and choose the same name to paint the window.',
  ],
  // the free themes on Firefox Add-ons ( addons.mozilla.org ), a store of its own like the Chrome Web Store
  firefox: [
    'Open the theme\'s Firefox Add-ons page with the Install from Firefox Add-ons button.',
    'Click Add to Firefox and confirm in the prompt that opens.',
    'The theme applies at once: the tabs, the toolbar and the address bar take its colors, no restart.',
    'Want another look later? Open Add-ons and themes ( Cmd+Shift+A on a Mac, Ctrl+Shift+A on Windows and Linux ) → Themes and enable another theme; switch back the same way any time.',
  ],
};

/** 'sublime-text' | 'Sublime Text' | 'sublime text' -> the steps, [] when unknown. */
export function gettingStartedFor( platform: string | undefined | null ): string[] {
  const key = ( platform || '' ).toLowerCase().replace( /-/g, ' ' ).trim();
  return GETTING_STARTED[ key ] ?? [];
}

/** 'sublime-text' -> 'Sublime Text', 'macos-terminal' -> 'macOS Terminal'. */
export function platformDisplayName( platformKey: string | undefined | null ): string {
  const key = ( platformKey || '' ).toLowerCase().replace( /-/g, ' ' ).trim();
  const special: { [ key: string ]: string } = {
    'macos terminal': 'macOS Terminal',
    macos: 'macOS',
    iterm2: 'iTerm2',
    'vs code': 'VS Code',
    jetbrains: 'JetBrains',
  };
  if ( special[ key ] ) return special[ key ];
  return key.split( ' ' ).filter( Boolean ).map( w => w.charAt( 0 ).toUpperCase() + w.slice( 1 ) ).join( ' ' );
}
